import { Outlet, useNavigate } from 'react-router-dom'
import { Menu, X } from 'lucide-react'
import { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useTranslation } from 'react-i18next'

export type LearningExitHandler = () => void
export type LearningMenuHandler = () => void

export interface LearningTopContext {
  setLessonLabel: (v: string) => void
  setStepLabel: (v: string) => void
  setProgressDots: (v: { total: number; currentIndex: number; doneIndices: number[] }) => void
  setOnExit: (handler: LearningExitHandler | null) => void
  setOnMenu: (handler: LearningMenuHandler | null) => void
}
const LearningCtx = createContext<LearningTopContext | null>(null)

export function useLearningTop() {
  const ctx = useContext(LearningCtx)
  if (!ctx) throw new Error('useLearningTop must be used inside <LearningLayout />')
  return ctx
}

export default function LearningLayout() {
  const { t } = useTranslation('learner')
  const navigate = useNavigate()
  const [lessonLabel, setLessonLabel] = useState('')
  const [stepLabel, setStepLabel] = useState('')
  const [progress, setProgressDots] = useState<{
    total: number; currentIndex: number; doneIndices: number[]
  }>({ total: 0, currentIndex: -1, doneIndices: [] })

  const onExitRef = useRef<LearningExitHandler | null>(null)
  const onMenuRef = useRef<LearningMenuHandler | null>(null)
  const [hasMenu, setHasMenu] = useState(false)

  const setOnExit = useCallback((handler: LearningExitHandler | null) => {
    onExitRef.current = handler
  }, [])
  const setOnMenu = useCallback((handler: LearningMenuHandler | null) => {
    onMenuRef.current = handler
    setHasMenu(handler !== null)
  }, [])

  const ctx = useMemo<LearningTopContext>(
    () => ({ setLessonLabel, setStepLabel, setProgressDots, setOnExit, setOnMenu }),
    [setOnExit, setOnMenu],
  )

  const handleExitClick = () => {
    if (onExitRef.current) {
      onExitRef.current()
    } else {
      navigate(-1)
    }
  }

  const handleMenuClick = () => {
    onMenuRef.current?.()
  }

  return (
    <LearningCtx.Provider value={ctx}>
      <div className="flex h-dvh min-h-screen flex-col overflow-hidden bg-lc-bg-secondary">
        <header className="flex h-[72px] shrink-0 items-center justify-between bg-lc-v3-gray-950 px-4 text-lc-text-inverse sm:px-6 lg:px-10">
          <div className="flex min-w-0 items-center gap-4">
            {hasMenu ? (
              <button
                type="button"
                onClick={handleMenuClick}
                aria-label={t('aria.menu')}
                className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-lc-v3-gray-900 hover:bg-lc-v3-gray-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500 md:hidden"
              >
                <Menu className="size-4" aria-hidden />
              </button>
            ) : null}

            {progress.total > 0 ? (
              <div
                className="inline-flex size-8 shrink-0 items-center justify-center rounded-[10px] bg-lc-v3-gray-900 text-lc-v3-body-1 font-bold"
                aria-label={t('aria.progress')}
              >
                {progress.currentIndex >= 0 ? progress.currentIndex + 1 : progress.total}
              </div>
            ) : null}

            <div className="min-w-0">
              <div className="truncate text-lc-v3-caption-1 font-medium text-[#aeaeb2]">
                <ContentMetadataText text={lessonLabel} />
              </div>
              <div className="mt-1 truncate text-lc-v3-body-1 font-bold">
                <ContentMetadataText text={stepLabel} />
              </div>
            </div>
          </div>

          <button
            type="button"
            onClick={handleExitClick}
            aria-label={t('aria.exit')}
            className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-lc-v3-gray-900 hover:bg-lc-v3-gray-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500"
          >
            <X className="size-4" aria-hidden />
          </button>
        </header>

        <main className="flex-1 flex min-h-0">
          <Outlet />
        </main>
      </div>
    </LearningCtx.Provider>
  )
}
